// ElectricalForm.js — "Report Details"
// Front matter and sign-off for the electrical recertification form:
// building info (item 1), inspection details (item 2), additional comments and
// the overall result. Every numbered form item (3 onward) lives in Photo Manager.

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, setDoc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { useAuth } from '../context/AuthContext';
import { getReportItems, LEAD_ITEMS } from '../reportItems';
import { loadReport, isConfirmed, slotHasRepair } from '../reportSlots';
import Sidebar from '../components/Sidebar';
import SignerPicker from '../components/SignerPicker';
import { rosterFromUser } from '../disciplines';
import { section1Fields, fieldValue, referenceField, noticeItem } from '../projectFields';
import Footer from '../components/Footer';
import '../App.css';

// ─── Overall result ───────────────────────────────────────────────────────────
// Only the conditions printed on the jurisdiction's page 1. Broward page 1 has
// no condition boxes, only repairs required Yes/No, which comes from the
// Report Editor.

// Wording as printed on each form's page 1
const OVERALL_OPTIONS = [
  { val: 'dangerous',   label: 'Dangerous Condition Observed. Notify Building Official within 10 days',                   color: '#f97316' },
  { val: 'immediate',   label: 'Immediate Dangerous Condition Observed. Notify Building and Fire Officials within 24 hours', color: '#ef4444' },
  { val: 'maintenance', label: 'Maintenance needed but does not rise to the level of Dangerous',                         color: '#fde68a' },
  { val: 'passed',      label: 'Passed the Inspection',                                                                   color: '#6ee7b7' },
];
const BOCA_LABELS = {
  dangerous:   'Dangerous Conditions Observed Notify Building Official within 10 days',
  immediate:   'Immediate Dangerous Conditions Observed Notify Building and Fire Official within 24 hours',
  maintenance: 'Maintenance needed but does not rise to the level of dangerous. No repairs needed for recertification purposes',
  passed:      'Passed the inspection / no repairs required',
};

// ─── Standard occupancy / repairs text ────────────────────────────────────────
// Goes in Broward 2d, Boca 2d2 and Miami-Dade / City of Miami 2e1. Switches
// with the Photo Manager findings until the engineer edits it.

const STANDARD_WITH_REPAIRS = 'The building is electrically safe for continued use under its present occupancy while the repair items identified herein are performed. Client or contractor shall apply for all necessary permits.';
const STANDARD_NO_REPAIRS   = 'No electrical deficiencies observed. The building is electrically safe for continued use under its present occupancy.';
const isStandardText = (t) => {
  const v = (t || '').trim();
  return v === '' || v === STANDARD_WITH_REPAIRS || v === STANDARD_NO_REPAIRS;
};

const getJurisdictionFlags = (county) => {
  const c = (county || '').toLowerCase();
  return {
    isBroward:   !c.includes('miami') && !c.includes('boca') && !c.includes('boynton'),
    isMiamiLike: c.includes('miami'),
  };
};

// ─── Component ────────────────────────────────────────────────────────────────

const ElectricalForm = () => {
  const { projectId }   = useParams();
  const { currentUser } = useAuth();
  const navigate        = useNavigate();

  const [project, setProject]                           = useState(null);
  const [formData, setFormData]                         = useState({});
  const [overallResult, setOverallResult]               = useState('');
  const [additionalComments, setAdditionalComments]     = useState('');
  const [occupancyExplanation, setOccupancyExplanation] = useState('');
  const [repairsDescription, setRepairsDescription]     = useState('');
  const [reportType, setReportType]                     = useState('');
  const [recertType, setRecertType]                     = useState('');
  const [inspectionDate, setInspectionDate]             = useState('');
  const [repairPermit, setRepairPermit]                 = useState('');
  const [reportSlots, setReportSlots]                   = useState({});
  const [roster, setRoster]                             = useState({ engineers: [], firms: [] });
  const [signer, setSigner]                             = useState({ engineerId: '', firmId: '' });
  const [saveStatus, setSaveStatus]                     = useState('');
  const [loading, setLoading]                           = useState(true);

  // ─── Load ───────────────────────────────────────────────────────────────────

  useEffect(() => {
    const load = async () => {
      try {
        const projectDoc = await getDoc(doc(db, 'projects', projectId));
        const proj = projectDoc.exists() ? projectDoc.data() : null;
        setProject(proj);

        const report = await loadReport(projectId, proj?.county);
        setReportSlots(report.slots);

        const formDoc = await getDoc(doc(db, 'projects', projectId, 'forms', 'electrical'));
        if (formDoc.exists()) {
          const data = formDoc.data();
          setFormData(data.sections || {});
          setOverallResult(data.overallResult || '');
          setAdditionalComments(data.additionalComments || '');
          setOccupancyExplanation(data.occupancyExplanation || '');
          setRepairsDescription(data.repairsDescription || '');
          setReportType(data.reportType || '');
          setRecertType(data.recertType || '');
          setInspectionDate(data.inspectionDate || '');
          setRepairPermit(data.repairPermit || '');
          setSigner({ engineerId: data.engineerId || '', firmId: data.firmId || '' });
        }

        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        setRoster(rosterFromUser(userDoc.exists() ? userDoc.data() : {}));
      } catch (err) {
        console.error('Error loading report details:', err);
      }
      setLoading(false);
    };
    load();
  }, [projectId, currentUser]);

  // ─── Auto-save ──────────────────────────────────────────────────────────────

  const buildSavePayload = useCallback(() => ({
    sections: formData,
    overallResult,
    additionalComments,
    occupancyExplanation,
    repairsDescription,
    reportType,
    recertType,
    inspectionDate,
    repairPermit,
    engineerId: signer.engineerId,
    firmId: signer.firmId,
    updatedAt: serverTimestamp(),
    updatedBy: currentUser.uid,
  }), [formData, overallResult, additionalComments, occupancyExplanation, repairsDescription, reportType, recertType, inspectionDate, repairPermit, signer, currentUser]);

  useEffect(() => {
    if (loading) return;
    const timer = setTimeout(async () => {
      try {
        await setDoc(doc(db, 'projects', projectId, 'forms', 'electrical'), buildSavePayload(), { merge: true });
        setSaveStatus('Auto-saved');
        setTimeout(() => setSaveStatus(''), 2000);
      } catch (err) { console.error('Auto-save error:', err); }
    }, 1500);
    return () => clearTimeout(timer);
  }, [loading, projectId, buildSavePayload]);

  const handleSave = async () => {
    setSaveStatus('Saving...');
    try {
      await setDoc(doc(db, 'projects', projectId, 'forms', 'electrical'), buildSavePayload(), { merge: true });
      setSaveStatus('Saved ✓');
      setTimeout(() => setSaveStatus(''), 2000);
    } catch (err) { setSaveStatus('Save failed'); }
  };

  const handleDeleteProject = async () => {
    if (!window.confirm('Remove this project from your list? The data will be preserved.')) return;
    try {
      await updateDoc(doc(db, 'projects', projectId), { deleted: true });
      navigate('/projects');
    } catch (err) { console.error('Error deleting project:', err); }
  };

  // ─── Derived ────────────────────────────────────────────────────────────────

  const { isBroward, isMiamiLike } = getJurisdictionFlags(project?.county);
  const isBoca = (project?.county || '').toLowerCase().includes('boca');
  const reportItems = useMemo(() => getReportItems(project?.county), [project]);
  const allKeys     = useMemo(() => [...LEAD_ITEMS.map(l => l.key), ...reportItems.map(i => i.key)], [reportItems]);
  const confirmedCount = allKeys.filter(k => isConfirmed(reportSlots[k])).length;

  const hasRepairs = reportItems.some(it => slotHasRepair(reportSlots[it.key], it));

  // Repairs required Yes/No comes from the Report Editor: Yes if any item needs repair
  const needsRepairs      = hasRepairs;
  const repairsYesNoLabel = needsRepairs ? 'Yes' : 'No';
  const overallValid      = OVERALL_OPTIONS.some(o => o.val === overallResult);

  // Standard occupancy / repairs text — Broward 2d, others 2e1 / 2d2
  const standardTarget = isBroward ? 'repairsDescription' : 'occupancyExplanation';
  const standardText   = hasRepairs ? STANDARD_WITH_REPAIRS : STANDARD_NO_REPAIRS;
  useEffect(() => {
    if (loading || !project) return;
    if (standardTarget === 'repairsDescription') {
      if (isStandardText(repairsDescription) && repairsDescription !== standardText) setRepairsDescription(standardText);
    } else if (isStandardText(occupancyExplanation) && occupancyExplanation !== standardText) {
      setOccupancyExplanation(standardText);
    }
  }, [loading, project, standardTarget, standardText, repairsDescription, occupancyExplanation]);

  if (loading) return <div style={{ marginLeft: '220px', padding: '2rem' }}>Loading...</div>;

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <div>
      <Sidebar />
      <div style={styles.container}>

        <div style={styles.titleRow}>
          <div>
            <h2 style={styles.title}>Project Details</h2>
            {project && (
              <p style={styles.subtitle}>
                {project.address}{project.city ? `, ${project.city}` : ''} — {project.county}
              </p>
            )}
          </div>
          <div style={styles.titleButtons}>
            <span style={styles.saveStatus}>{saveStatus}</span>
            <button type="button" style={styles.saveButton}   onClick={handleSave}>Save</button>
            <button type="button" style={styles.editButton}   onClick={() => navigate(`/project/${projectId}/edit`)}>Edit Project</button>
            <button type="button" style={styles.deleteButton} onClick={handleDeleteProject}>Remove Project</button>
          </div>
        </div>

        <div style={styles.photoManagerBanner}>
          <span>
            Form items 3 onward (status, findings, service size, panel table) are completed in <strong>Report Editor</strong>.
            {' '}{confirmedCount} of {allKeys.length} slots confirmed.
          </span>
          <button type="button" style={styles.photoManagerLink} onClick={() => navigate(`/project/${projectId}/photos`)}>
            Open Report Editor
          </button>
        </div>

        {/* ── Engineer and Firm ── */}
        <div style={styles.card}>
          <h3 style={styles.sectionTitle}>Engineer and Firm</h3>
          <SignerPicker roster={roster} engineerId={signer.engineerId} firmId={signer.firmId} onChange={setSigner} />
        </div>

        {/* ── 1. Building Information ── */}
        <div style={styles.card}>
          <h3 style={styles.sectionTitle}>1. Building Information</h3>
          <div style={styles.infoGrid}>
            {project && referenceField(project.county) && (
              <div>
                <span style={styles.propLabel}>{referenceField(project.county).label}:</span>{' '}
                <span style={styles.propValue}>{project[referenceField(project.county).key] || '—'}</span>
              </div>
            )}
            {project && section1Fields(project.county, ['electrical']).map(f => (
              <div key={f.key}>
                <span style={styles.propLabel}>{f.item}. {f.label}:</span>{' '}
                <span style={styles.propValue}>{fieldValue(project, f) || '—'}</span>
              </div>
            ))}
            {project && (
              <div>
                <span style={styles.propLabel}>{noticeItem(project.county)}. Date of Notice:</span>{' '}
                <span style={styles.propValue}>{project.noticeDate || '—'}</span>
              </div>
            )}
          </div>
          <p style={styles.hintText}>From the project. Change these with Edit Project.</p>
        </div>

        {/* ── 2. Inspection Details ── */}
        <div style={styles.card}>
          <h3 style={styles.sectionTitle}>2. Inspection Details</h3>
          <div style={styles.row}>
            <div style={styles.field}>
              <label style={styles.label} htmlFor="report-type">Report Type</label>
              <select id="report-type" value={reportType} onChange={e => setReportType(e.target.value)} style={styles.input}>
                <option value="">— Select —</option>
                <option value="Initial Inspection Report">Initial Inspection Report</option>
                <option value="Amended Inspection Report">Amended Inspection Report</option>
              </select>
            </div>
            <div style={styles.field}>
              <label style={styles.label} htmlFor="insp-date">Inspection Date</label>
              <input id="insp-date" type="date" value={inspectionDate} onChange={e => setInspectionDate(e.target.value)} style={styles.input} />
            </div>
            {isBoca && reportType === 'Amended Inspection Report' && (
              <div style={styles.field}>
                <label style={styles.label} htmlFor="repair-permit">Repair Permit # (page 1, amended report)</label>
                <input id="repair-permit" value={repairPermit} onChange={e => setRepairPermit(e.target.value)} style={styles.input} />
              </div>
            )}
          </div>

          {/* 2d — Repairs required */}
          <div style={styles.subBox}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
              <span style={{ ...styles.label, margin: 0 }}>d. Are any electrical repairs required?</span>
              <span style={{
                padding: '0.2rem 0.75rem', borderRadius: '4px', fontSize: '0.85rem', fontWeight: '600',
                backgroundColor: needsRepairs ? '#fca5a5' : '#6ee7b7', color: '#111',
              }}>{repairsYesNoLabel}</span>
              <span style={{ fontSize: '0.75rem', color: '#4b5563', fontStyle: 'italic' }}>(from Report Editor repair items)</span>
            </div>
            <label style={styles.label} htmlFor="repairs-desc">If required — describe and indicate acceptance</label>
            {standardTarget === 'repairsDescription' && isStandardText(repairsDescription) && (
              <p style={styles.hintText}>Standard text — switches with the Report Editor findings until you edit it.</p>
            )}
            <textarea
              id="repairs-desc"
              style={{ ...styles.textarea, marginTop: '0.4rem' }}
              rows={3}
              value={repairsDescription}
              onChange={e => setRepairsDescription(e.target.value)}
            />
          </div>

          {/* 2e — Occupancy explanation (Miami-Dade, City of Miami, Boca) */}
          {!isBroward && (
            <div style={{ marginTop: '0.75rem' }}>
              <label style={styles.label} htmlFor="occupancy">
                {isMiamiLike ? 'e. ' : ''}Can the building continue to be occupied during repairs? — Explanation / Conditions
              </label>
              {standardTarget === 'occupancyExplanation' && isStandardText(occupancyExplanation) && (
                <p style={styles.hintText}>Standard text — switches with the Report Editor findings until you edit it.</p>
              )}
              <textarea
                id="occupancy"
                style={{ ...styles.textarea, marginTop: '0.4rem' }}
                rows={3}
                value={occupancyExplanation}
                onChange={e => setOccupancyExplanation(e.target.value)}
              />
            </div>
          )}
        </div>

        {/* ── End-of-form comments: Miami 29, Boca 25 (Broward has none; its 1m is on the project) ── */}
        {!isBroward && <div style={styles.card}>
          <h3 style={styles.sectionTitle}>{isMiamiLike ? '29. General Additional Comments' : '25. Additional Comments'}</h3>
          <p style={styles.hintText}>Printed directly into the report. Use for general observations or items not covered elsewhere.</p>
          <textarea
            aria-label="Additional comments"
            style={{ ...styles.textarea, minHeight: '120px' }}
            rows={6}
            value={additionalComments}
            onChange={e => setAdditionalComments(e.target.value)}
          />
        </div>}

        {/* ── Overall Result (page 1) ── */}
        <div style={styles.card}>
          <h3 style={styles.sectionTitle}>Overall Inspection Result (page 1)</h3>
          {isBroward ? (
            <p style={styles.hintText}>
              Broward page 1 has no condition boxes. It marks repairs required <strong>{repairsYesNoLabel}</strong> from the Report Editor repair items.
            </p>
          ) : (
            <>
              <p style={styles.hintText}>Please check the condition that applies (as printed on the form):</p>
              <div style={{ ...styles.overallButtons, flexDirection: 'column', alignItems: 'stretch', marginTop: '0.5rem' }}>
                {OVERALL_OPTIONS.map(o => (
                  <button key={o.val} type="button" aria-pressed={overallResult === o.val}
                    style={{
                      ...styles.overallButton, textAlign: 'left',
                      backgroundColor: overallResult === o.val ? o.color : '#f3f4f6',
                      fontWeight: overallResult === o.val ? '600' : '400',
                      color: overallResult === o.val && (o.val === 'dangerous' || o.val === 'immediate') ? 'white' : '#111',
                    }}
                    onClick={() => setOverallResult(o.val)}
                  >{(isBoca && BOCA_LABELS[o.val]) || o.label}</button>
                ))}
              </div>
              {!overallValid && <p style={{ ...styles.hintText, color: '#7f1d1d' }}>Select the condition that applies.</p>}
            </>
          )}
        </div>

        <div style={styles.bottomActions}>
          <button type="button" style={styles.saveButton}   onClick={handleSave}>Save</button>
          <button type="button" style={styles.photosButton} onClick={() => navigate(`/project/${projectId}/report`)}>Generate Report</button>
        </div>

      </div>
      <Footer />
    </div>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = {
  container:          { marginLeft: '200px', padding: '2rem', paddingBottom: '5rem' },
  titleRow:           { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem', gap: '1rem', flexWrap: 'wrap' },
  title:              { margin: '0 0 0.25rem', color: '#112b49', fontSize: '1.5rem' },
  subtitle:           { margin: 0, color: '#555', fontSize: '0.9rem' },
  titleButtons:       { display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' },
  saveStatus:         { fontSize: '0.8rem', color: '#4b5563', fontStyle: 'italic' },
  saveButton:         { padding: '0.5rem 0', width: '120px', backgroundColor: '#15803d', color: 'white', border: 'none', borderRadius: '5px', fontSize: '0.95rem', fontWeight: '700', cursor: 'pointer', fontFamily: 'inherit' },
  photosButton:       { padding: '0.5rem 1rem', backgroundColor: '#112b49', color: 'white', border: 'none', borderRadius: '5px', fontSize: '0.95rem', fontWeight: '700', cursor: 'pointer', fontFamily: 'inherit' },
  deleteButton:       { padding: '0.5rem 0', width: '140px', backgroundColor: 'rgb(229,9,20)', color: 'white', border: 'none', borderRadius: '5px', fontSize: '0.95rem', fontWeight: '700', cursor: 'pointer', fontFamily: 'inherit' },
  editButton:         { padding: '0.5rem 0', width: '120px', backgroundColor: '#b45309', color: 'white', border: 'none', borderRadius: '5px', fontSize: '0.95rem', fontWeight: '700', cursor: 'pointer', fontFamily: 'inherit' },
  card:               { backgroundColor: 'rgba(255,255,255,0.92)', borderRadius: '8px', padding: '1.5rem', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', marginBottom: '1.25rem' },
  sectionTitle:       { margin: '0 0 1rem', fontSize: '1rem', fontWeight: '600', color: '#112b49', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid #e5e7eb', paddingBottom: '0.5rem' },
  hintText:           { margin: '0.25rem 0 0', fontSize: '0.8rem', color: '#4b5563' },
  photoManagerLink:   { padding: '0.4rem 0.9rem', backgroundColor: '#ffffff', color: '#112b49', border: '1px solid #112b49', borderRadius: '4px', fontSize: '0.85rem', cursor: 'pointer', fontFamily: 'inherit' },
  photoManagerBanner: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', backgroundColor: 'rgba(100,149,237,0.1)', border: '1px solid rgba(100,149,237,0.35)', borderRadius: '8px', padding: '0.75rem 1rem', marginBottom: '1rem', fontSize: '0.875rem', color: '#112b49' },
  infoGrid:           { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '0.5rem', fontSize: '0.875rem' },
  propLabel:          { fontWeight: '600', color: '#112b49' },
  propValue:          { color: '#374151' },
  row:                { display: 'flex', gap: '1rem', flexWrap: 'wrap' },
  field:              { display: 'flex', flexDirection: 'column', gap: '0.25rem', flex: 1, minWidth: '180px', marginBottom: '0.75rem' },
  label:              { fontSize: '0.875rem', fontWeight: '500', color: '#374151' },
  input:              { padding: '0.5rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.9rem', fontFamily: 'inherit', outline: 'none', width: '100%', boxSizing: 'border-box' },
  subBox:             { marginTop: '0.75rem', padding: '0.75rem', backgroundColor: '#f9fafb', borderRadius: '6px', border: '1px solid #e5e7eb' },
  textarea:           { width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.875rem', fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box', outline: 'none' },
  overallButtons:     { display: 'flex', gap: '0.75rem', flexWrap: 'wrap' },
  overallButton:      { padding: '0.65rem 1.25rem', border: '1px solid #d1d5db', borderRadius: '5px', fontSize: '0.9rem', cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s' },
  bottomActions:      { display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '1rem', paddingBottom: '2rem' },
};

export default ElectricalForm;
